import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Box, Avatar, Menu, MenuItem, Divider, useMediaQuery, Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HistoryIcon from '@mui/icons-material/History';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTheme } from '@mui/material/styles';
import { useAuthStore, hasAnyRole } from '../auth/store';
import { authApi } from '../auth/api';
import { useDashboardKpis } from '../hooks/useDashboardKpis';

const DRAWER_W = 240;

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: <DashboardIcon />, roles: ['ADMIN', 'OPERADOR', 'CONSULTA'] },
  { to: '/vehicles', label: 'Camionetas', icon: <DirectionsCarIcon />, roles: ['ADMIN', 'OPERADOR', 'CONSULTA'] },
  { to: '/drivers', label: 'Choferes', icon: <PeopleIcon />, roles: ['ADMIN', 'OPERADOR', 'CONSULTA'] },
  { to: '/history', label: 'Historial', icon: <HistoryIcon />, roles: ['ADMIN', 'OPERADOR', 'CONSULTA'] },
  { to: '/upload', label: 'Subir lectura', icon: <CloudUploadIcon />, roles: ['ADMIN', 'OPERADOR'] },
  { to: '/reports', label: 'Reportes', icon: <AssessmentIcon />, roles: ['ADMIN', 'OPERADOR', 'CONSULTA'] },
  { to: '/admin/manual-review', label: 'Revisión manual', icon: <ReportProblemIcon />, roles: ['ADMIN', 'OPERADOR'] },
  { to: '/admin/users', label: 'Usuarios', icon: <AdminPanelSettingsIcon />, roles: ['ADMIN'] },
];

export function Layout() {
  const nav = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null);
  const { user, refreshToken, clear } = useAuthStore();
  const kpis = useDashboardKpis();

  const visible = navItems.filter((it) => hasAnyRole(user, ...it.roles));

  const drawer = (
    <Box sx={{ width: DRAWER_W }} role="navigation">
      <Toolbar><Typography variant="h6">Flotillas</Typography></Toolbar>
      <Divider />
      <List>
        {visible.map((it) => (
          <ListItem key={it.to} disablePadding>
            <ListItemButton component={NavLink} to={it.to} onClick={() => setDrawerOpen(false)}>
              <ListItemIcon>{it.icon}</ListItemIcon>
              <ListItemText primary={it.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const logout = async () => {
    if (refreshToken) {
      try { await authApi.logout(refreshToken); } catch { /* ignore */ }
    }
    clear();
    nav('/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar>
          {isMobile && (
            <IconButton color="inherit" edge="start" onClick={() => setDrawerOpen(true)} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Flotillas</Typography>
          {kpis.data && (
            <Chip
              label={`${kpis.data.insideNow} adentro`}
              color="success"
              size="small"
              sx={{ mr: 2, color: '#fff', bgcolor: 'success.dark' }}
            />
          )}
          <IconButton onClick={(e) => setMenuEl(e.currentTarget)} color="inherit">
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
              {user?.fullName?.[0]?.toUpperCase() ?? '?'}
            </Avatar>
          </IconButton>
          <Menu anchorEl={menuEl} open={Boolean(menuEl)} onClose={() => setMenuEl(null)}>
            <MenuItem disabled>{user?.email}</MenuItem>
            <Divider />
            <MenuItem onClick={logout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon> Cerrar sesión
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {isMobile ? (
        <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>{drawer}</Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_W, flexShrink: 0,
            '& .MuiDrawer-paper': { width: DRAWER_W, boxSizing: 'border-box' },
          }}
        >
          {drawer}
        </Drawer>
      )}

      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 8, maxWidth: '100%', overflow: 'auto' }}>
        <Outlet />
      </Box>
    </Box>
  );
}
